import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { IDR } from "@/lib/format";
import { inputClass, labelClass, btnGhost } from "@/components/Brand";
import { rowKey } from "@/lib/rowKey";

/** Manual charge entry and the existing charge list. */
export const ChargesPanel = ({ charges, charge, setCharge, onAdd, onRemove }) => {
  const { t } = useI18n();

  return (
    <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-6" data-testid="charges-panel">
      <p className="eyebrow mb-5">{t("custom_charges")}</p>
      <form onSubmit={onAdd} className="space-y-4">
        <input data-testid="charge-label-input" className={inputClass} placeholder={t("charge_label")}
          value={charge.label} onChange={(e) => setCharge({ ...charge, label: e.target.value })} />
        <input data-testid="charge-amount-input" type="number" className={inputClass} placeholder={t("amount")}
          value={charge.amount} onChange={(e) => setCharge({ ...charge, amount: e.target.value })} />
        <button data-testid="add-charge-btn" className={`${btnGhost} w-full py-3`}>{t("add_charge")}</button>
      </form>
      <div className="mt-5 space-y-2">
        {(charges || []).map((c) => (
          <div key={c.charge_id} className="flex items-center justify-between text-sm border-b border-[#1E1E1E] pb-2">
            <span className="text-[#A8A8A8]">{c.label}</span>
            <span className="flex items-center gap-3">
              <span className="font-mono-accent text-xs">{IDR(c.amount)}</span>
              <button data-testid={`remove-charge-${c.charge_id}`} onClick={() => onRemove(c.charge_id)}
                className="text-[#5A5A5A] hover:text-[#8B0000]">
                <Trash2 size={13} />
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/** Owner-only invoice breakdown, with named deductions the studio can stack. */
export const InvoicePanel = ({ invoice, discounts, onSaveDiscounts, busy }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState(
    (discounts || []).length ? discounts.map((d) => ({ ...d, _k: rowKey() })) : [{ label: "", amount: "", _k: rowKey() }]
  );

  const update = (idx, patch) => setLines((ls) => ls.map((l, n) => (n === idx ? { ...l, ...patch } : l)));
  const activeCount = (discounts || []).filter((d) => Number(d.amount) > 0).length;

  return (
    <div className="border border-[#D4AF37]/30 bg-[#0F0F0F] p-6" data-testid="invoice-panel">
      <p className="eyebrow mb-5">{t("invoice")}</p>
      {invoice.items.map((i) => (
        <div key={`${i.label}-${i.qty}-${i.amount}`}
          className="flex justify-between text-sm py-1.5 border-b border-[#1E1E1E]">
          <span className={i.kind === "discount" ? "text-[#D4AF37]" : "text-[#A8A8A8]"}>{i.label} × {i.qty}</span>
          <span className={`font-mono-accent text-xs ${i.kind === "discount" ? "text-[#D4AF37]" : ""}`}>
            {i.amount < 0 ? `− ${IDR(Math.abs(i.amount))}` : IDR(i.amount)}
          </span>
        </div>
      ))}
      <div className="flex justify-between mt-4 pt-3 border-t border-[#D4AF37]/40">
        <span className="text-[11px] tracking-[0.18em] uppercase">{t("total")}</span>
        <span data-testid="invoice-total" className="font-mono-accent text-sm text-[#D4AF37]">{IDR(invoice.total)}</span>
      </div>

      {open ? (
        <div className="mt-6 border-t border-[#1E1E1E] pt-5 space-y-5" data-testid="discount-editor">
          {lines.map((line, idx) => (
            <div key={line._k} className="space-y-3 border-l-2 border-[#2A2A2A] pl-4">
              <div>
                <label className={labelClass}>{t("discount_label")}</label>
                <input data-testid={`discount-label-input-${idx}`} className={inputClass} value={line.label}
                  onChange={(e) => update(idx, { label: e.target.value })} placeholder={t("discount_placeholder")} />
              </div>
              <div>
                <label className={labelClass}>{t("discount_amount")}</label>
                <input data-testid={`discount-amount-input-${idx}`} type="number" min="0" className={inputClass}
                  value={line.amount} onChange={(e) => update(idx, { amount: e.target.value })} placeholder="50000" />
              </div>
              {lines.length > 1 && (
                <button data-testid={`remove-discount-line-${idx}`}
                  onClick={() => setLines((ls) => ls.filter((_, n) => n !== idx))}
                  className="inline-flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-[#707070] hover:text-[#8B0000]">
                  <Trash2 size={12} /> {t("remove")}
                </button>
              )}
            </div>
          ))}
          <button data-testid="add-discount-line-btn"
            onClick={() => setLines((ls) => [...ls, { label: "", amount: "", _k: rowKey() }])}
            className="text-[10px] tracking-[0.18em] uppercase text-[#D4AF37] hover:underline">
            + {t("add_discount_line")}
          </button>
          <div className="flex flex-wrap gap-3">
            <button data-testid="save-discount-btn" disabled={busy}
              onClick={async () => {
                await onSaveDiscounts(lines
                  .filter((l) => Number(l.amount) > 0)
                  .map((l) => ({ amount: Number(l.amount), label: l.label })));
                setOpen(false);
              }}
              className={`${btnGhost} flex-1`}>
              {t("apply_discount")}
            </button>
            <button data-testid="cancel-discount-btn" onClick={() => setOpen(false)}
              className="text-[10px] tracking-[0.18em] uppercase text-[#707070] hover:text-[#F3F3F1] px-3">
              {t("cancel")}
            </button>
          </div>
        </div>
      ) : (
        <button data-testid="open-discount-btn" onClick={() => setOpen(true)}
          className="mt-5 inline-flex items-center min-h-[44px] text-[10px] tracking-[0.18em] uppercase text-[#D4AF37] hover:underline">
          {activeCount ? t("edit_discount") : t("add_discount")}
        </button>
      )}
    </div>
  );
};
